import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Spinner, Tab, Tabs } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { getUsersSkills, getUsersSkillsAudit, putUsersSkills } from '../../api/skillsMatrix';
import SkillsMatrixDisplayField from './SkillsMatrixDisplayField';
import SkillsMatrixErrorTab from './SkillsMatrixErrorTab';
import UserAudit from './UserAudit';
import UserSkills from './UserSkills';

const TabKeys = Object.freeze({
  USER: 'user',
  ADMIN: 'admin',
});

const SkillsMatrixUser = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  if (!id) {
    navigate('/SkillsMatrix');
  }

  const [tab, setTab] = useState(TabKeys.USER);
  const [skillLevelInput, setSkillLevelInput] = useState(null);
  const [dutyInput, setDutyInput] = useState(null);

  const {
    isPending: userSkillsPending,
    isRefetching: userSkillsRefetching,
    data: userSkills,
    error: userSkillsError,
    refetch: refetchUser,
  } = useQuery({
    queryKey: ['getUserSkills', id],
    queryFn: () => getUsersSkills(id),
  });

  if (userSkillsError?.name === 'RangeError') {
    navigate('/SkillsMatrix');
  }

  const { firstName, lastName, userName, department, accessLevel, skillLevel, dutiableJobs, lastUpdated } =
    userSkills || {};

  const finalSkillLevel = skillLevelInput ?? skillLevel ?? 0;
  const finalDuty = dutyInput ?? dutiableJobs ?? false;
  const hasUpdatedData = finalSkillLevel !== skillLevel || finalDuty !== dutiableJobs;

  const {
    isPending: auditPending,
    data: auditLog,
    error: auditError,
    refetch: refetchAudit,
  } = useQuery({
    queryKey: ['getUserAudit', id],
    queryFn: () => getUsersSkillsAudit(id),
    enabled: tab === TabKeys.ADMIN,
  });

  const saveSkills = useMutation({
    mutationFn: async (user) => {
      await putUsersSkills(user);
      refetchUser();
      refetchAudit();
    },
  });

  //We only fetch audit after we select the tab, so use loading instead of pending to check if we can save
  const isAnyLoading = userSkillsPending || userSkillsRefetching || saveSkills.isPending;

  const onSave = async () => {
    await saveSkills.mutate({ skillLevel: finalSkillLevel, dutiableJobs: finalDuty, userId: id });
  };

  return (
    <div className="full-width-container skills-matrix">
      <section className="floating-box position-relative p-3" style={{ maxWidth: '1040px' }}>
        <div className="d-flex justify-content-between align-items-center pb-2">
          <h1 className="pb-2">Skills Matrix</h1>
          <SkillsMatrixDisplayField label={'Username'} value={userName} loading={userSkillsPending} />
        </div>
        <div className="pb-4">
          <Tabs activeKey={tab} onSelect={setTab}>
            <Tab eventKey={TabKeys.USER} title="User">
              {userSkillsError ? (
                <SkillsMatrixErrorTab
                  retry={() => {
                    refetchUser();
                  }}
                  loading={userSkillsPending}
                />
              ) : (
                <UserSkills
                  {...{ firstName, lastName, department, accessLevel, lastUpdated }}
                  loading={userSkillsPending}
                  skillLevel={finalSkillLevel}
                  setSkillLevel={setSkillLevelInput}
                  duty={finalDuty}
                  setDuty={setDutyInput}
                />
              )}
            </Tab>

            <Tab eventKey={TabKeys.ADMIN} title="Audit">
              {auditError ? (
                <SkillsMatrixErrorTab
                  retry={() => {
                    refetchAudit();
                  }}
                  loading={auditPending}
                />
              ) : (
                <UserAudit auditLog={auditLog} loading={auditPending} />
              )}
            </Tab>
          </Tabs>
        </div>
        <div className="d-flex justify-content-between">
          <Button variant="secondary" onClick={() => navigate(-1)}>
            Back
          </Button>

          <div className="d-flex gap-3">
            {!!saveSkills.error && (
              <div className={'pb-3 text-danger'}>
                <em>Failed to save user data</em>
              </div>
            )}
            <Button
              variant="primary"
              disabled={isAnyLoading || userSkillsError || !hasUpdatedData || tab !== TabKeys.USER}
              onClick={onSave}
            >
              {isAnyLoading ? <Spinner animation="border" size="sm" /> : 'Save'}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SkillsMatrixUser;
